/**
 * Shared layout styles for the public auth screens (T27.2.10.1 Phase B).
 *
 * The Create account / reset-request / reset / verify screens mirror the
 * LoginPage card layout so the whole auth surface reads as one unit. Token-only
 * (CP5) — every value is a design token, reused from the same vocabulary
 * LoginPage uses (it keeps its own local copies for the safe-area-inset
 * happy-dom workaround; these are the shared baseline for the new screens).
 */

import type { CSSProperties } from 'react';

export const authPageStyle: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--space-6) var(--space-4)',
  paddingTop: 'max(var(--space-6), env(safe-area-inset-top, 0px))',
  paddingBottom: 'max(var(--space-6), env(safe-area-inset-bottom, 0px))',
  background: 'var(--gradient-brand-backdrop)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  boxSizing: 'border-box',
};

export const authStackStyle: CSSProperties = {
  width: '100%',
  maxWidth: '24rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 'var(--space-4)',
};

export const authWordmarkRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  marginBottom: 'var(--space-2)',
};

export const authCardBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
};

export const authHeadingStyle: CSSProperties = {
  margin: 0,
  // T47 Phase 4 — auth page titles render the Poppins display face.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-xl)',
  fontWeight: 'var(--font-weight-semibold)',
  textAlign: 'center',
};

export const authSubheadingStyle: CSSProperties = {
  margin: 0,
  textAlign: 'center',
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-sm)',
};

export const authFormStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
};

export const authInlineNoteStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

export const authInlineErrorStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-status-danger)',
  margin: 0,
};

export const authSuccessNoteStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-status-success)',
  margin: 0,
};

export const authFooterRowStyle: CSSProperties = {
  margin: 0,
  textAlign: 'center',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
};

export const authLinkStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  textDecoration: 'underline',
};

// T49.3 — the signup consent row + its calm "unticked" hint. Homed here (not in
// CreateAccountPage) so the Login screen could reuse them if consent ever moves
// there (Designer handoff). The row is deliberately border-less + fill-less — a
// line of text with a tick, lighter than the bordered callouts, so it never reads
// as a warning. The hint is the muted note color (same as "Loading Google sign-in…"),
// NOT the danger red, with `role="status"` so AT announces it calmly.
export const authConsentRowStyle: CSSProperties = {
  margin: 0,
};

export const authConsentHintStyle: CSSProperties = {
  margin: 0,
  marginTop: 'var(--space-2)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
};

// T49.3 — the inline indigo link family for the [Terms]/[Privacy] nouns in the
// consent sentence. Matches the Privacy/Terms links already on the Login screen
// (indigo accent, underlined) so it reads as native auth chrome, not a second
// link style.
export const authConsentLinkStyle: CSSProperties = {
  color: 'var(--color-accent-primary)',
  textDecoration: 'underline',
};

// BUG42 — the consent "links" are now <button>s (they open an in-app overlay,
// not a navigation), so they must shed the browser's default button chrome and
// sit inline in the running sentence exactly like the old <a>. Spread AFTER
// `authConsentLinkStyle` so the indigo colour + underline win. `font: inherit`
// picks up the surrounding consent-label type; `cursor: pointer` keeps the link
// affordance. `minHeight: 0` overrides responsive.css's touch-width
// `button, [role='button'] { min-height: 2.75rem }` rule, which otherwise
// inflates this inline button into a 44px atomic box mid-sentence — the row
// itself is the tap target, not the inline link (WCAG 2.5.8 inline exception).
export const consentLinkButtonReset: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  cursor: 'pointer',
  display: 'inline',
  minHeight: 0,
};

// Basic well-formed-email check (mirrors the backend zod intent, catches typos
// client-side). Not a full RFC validator.
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const MIN_PASSWORD_LENGTH = 8;
